import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  generateColumnsFromData,
  generateRowsWithIds,
} from "../utils/constants";
import useApi from "./useApi";
import checkIfUserHasAccess from "../components/common/UserCheckAccess";
import canUserSeeThis from "../components/common/CheckCanUserSeeThis";

const useDocumentList = (config) => {
  const {
    docType = "INV",
    docLabel = "Invoice",
    p1Value = "TOP",
    p2Value = "ORDER_INV",
    docNoField = "INVNO",
    editRoute = "/sales_invoice",
    screenCode = "DOCOUNT",
    initialSearchType = "ALL",
  } = config;

  // Get configuration from localStorage
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const DEPARTMENT_NO =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const REPORT_HEADER_IMAGE = API_PATHS[0]?.REP_HEADER;
  const REPORT_FOOTER_IMAGE = API_PATHS[0]?.REP_FOOTER;
  const COMPANY_NAME = API_PATHS[0]?.COMPNAME;
  const CURRENT_USER = localStorage.getItem("currentUserName");
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "-";
  const CURRENT_USER_ROLE = CURRENT_USER_OBJECT[0]?.accessgrp || "-";
  const SALESMAN = CURRENT_USER_OBJECT[0]?.sales_man || "-";
  const hasMP = !checkIfUserHasAccess("MP");
  const hasA11 = !checkIfUserHasAccess("Z2"); //CORDINATOR ACCESS Invoice of the salesman only other list for all salesman's
  const salesmanParam =
    docType === "INV"
      ? hasMP ? SALESMAN : "-"
      : hasMP && !hasA11 ? SALESMAN : "-";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;
  const ZATCASTATUS = CURRENT_USER_OBJECT[0]?.VATMODE;
  const IMAGE_POST_PATH = API_PATHS[0]?.IMG_POST_PATH;
  const SERVER_PATH = API_PATHS[0]?.IMG_SERVERPATH;

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState(initialSearchType);
  const [showSidebar, setShowSidebar] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [documentData, setDocumentData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const navigate = useNavigate();
  const { request, loading } = useApi();

  // Fetch document list
  const fetchDocumentList = async (
    p1 = "TOP",
    p2 = p2Value,
    p3 = "-",
    query = "-",
  ) => {
    const safeQuery = encodeURIComponent(query || "-");
    const hasSearch = query !== "-";
    const effectivePage = hasSearch ? 1 : pageNumber;
    const effectiveSize = hasSearch ? 999999 : pageSize;
    const url = `${BASE_URL}/api/CRMDocListView/${COMPANY_CODE}/${docType}/${p1}/${p2}/${p3}/${safeQuery}/${salesmanParam}/${DEPARTMENT_NO}/${effectivePage}/${effectiveSize}`;

    try {
      const response = await request({
        url: url,
        method: "GET",
      });
      return response || [];
    } catch (err) {
      console.error(`Failed to fetch ${docLabel} details:`, err);
      return [];
    }
  };

  const fetchTotalCount = async (screenCode) => {
    if (!screenCode) return;

    const url = `${BASE_URL}/api/CRMDocListView/${COMPANY_CODE}/${screenCode}/-/-/-/-/${salesmanParam}/${DEPARTMENT_NO}/1/100`;
    // https://cubixweberp.com:213/api/CRMDocListView/SUNRISEWEB/SAMPLEISSUECOUNT/-/-/-/-/SUN00001/MAIN/1/100
    try {
      const response = await request({ url, method: "GET" });
      const count =
        response?.[0]?.CNT ?? response?.[0]?.Column1 ?? Number(response) ?? 0;
      setTotalCount(count);
    } catch (err) {
      console.error("Failed to fetch total count:", err);
      setTotalCount(0);
    }
  };

  // Function to manually refresh document list
  const refreshDocumentList = async () => {
    const p1 = p1Value;
    const p2 = p2Value;
    const p3 = searchType?.trim() === "" ? "-" : searchType;
    const query = searchTerm?.trim() === "" ? "-" : searchTerm.trim();

    const data = await fetchDocumentList(p1, p2, p3, query);
    setDocumentData(data);
    setRows(generateRowsWithIds(data));
    setColumns(generateColumnsFromData(data));

    // Also refresh the total count
    await fetchTotalCount(screenCode);
  };

  useEffect(() => {
    fetchTotalCount(screenCode);
  }, [screenCode, BASE_URL, COMPANY_CODE, DEPARTMENT_NO, docType]);

  // Fetch data when search term, type, pageNumber, pageSize, or refreshTrigger changes
  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      const p1 = p1Value;
      const p2 = p2Value;
      const p3 = searchType?.trim() === "" ? "-" : searchType;
      const query = searchTerm?.trim() === "" ? "-" : searchTerm.trim();

      const data = await fetchDocumentList(p1, p2, p3, query);
      setDocumentData(data);
      setRows(generateRowsWithIds(data));
      setColumns(generateColumnsFromData(data));
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [
    searchTerm,
    searchType,
    p2Value,
    pageNumber,
    pageSize,
    p1Value,
    refreshTrigger,
    docType,
  ]);

  const isSalesOrderEditable = async (soNo) => {
    if (!soNo) return false;

    const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/SOEDITSTATUS/${soNo}/${DEPARTMENT_NO}`;
    try {
      const response = await request({ url, method: "GET" });

      const status = response?.[0]?.Column1?.toUpperCase() ?? "";
      return status === "OPEN"; // editable only if status is OPEN
    } catch (err) {
      console.error("Failed to check if SO is editable:", err);
      return false;
    }
  };

  const isDeliveryOrderEditable = async (doNo) => {
    if (!doNo) return false;

    const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/DOEDITSTATUS/${doNo}/${DEPARTMENT_NO}`;
    try {
      const response = await request({ url, method: "GET" });

      const status = response?.[0]?.Column1?.toUpperCase() ?? "";
      return status === "OPEN"; // editable only if status is OPEN
    } catch (err) {
      console.error("Failed to check if DO is editable:", err);
      return false;
    }
  };

  // Handle edit navigation
  const handleEdit = async () => {
    if (!selectedRowData || !selectedRowData[docNoField]) {
      console.warn("No row selected or missing doc number field.");
      return;
    }

    const docNo = selectedRowData[docNoField];

    // Check if the document type is Sales Order
    if (docType === "SORD") {
      const editable = await isSalesOrderEditable(docNo);
      if (!editable) {
        alert(
          `Sales Order ${docNo} cannot be edited because a delivery order has already been created.`,
        );
        return;
      }
    }
    console.log("User has HZ access", canUserSeeThis("HZ"));

    // Check if the document type is Delivery Order and user has access to edit (Do modification after invoicing)
    if (docType === "DORD") {
      const editable = await isDeliveryOrderEditable(docNo);

      if (!editable) {
        if (canUserSeeThis("HZ")) {
          alert(
            `Delivery Order ${docNo} is already invoiced.\n\nYou have special access to modify it.`,
          );
          // Allow edit (do NOT return)
        } else {
          alert(
            `Delivery Order ${docNo} cannot be edited because an invoice has already been created.`,
          );
          return; // Block normal users
        }
      }
    }
    if (docType === "BTOUT") {
      const docStatus = selectedRowData["STATUS"]
      if (docStatus === "RECEIVED") {
        alert(
          `Branch Transfer Out ${docNo} cannot be edited because it has already been received.`,
        );
        return;
      }

    }

    navigate(editRoute, {
      state: { docNo, mode: "edit" },
    });
  };

  // Export all data matching current search filters
  const exportDocumentList = async () => {
    const p1 = p1Value;
    const p2 = p2Value;
    const p3 = searchType?.trim() === "" ? "-" : searchType;
    const query = searchTerm?.trim() === "" ? "-" : searchTerm.trim();
    const safeQuery = encodeURIComponent(query);

    const url = `${BASE_URL}/api/CRMDocListView/${COMPANY_CODE}/${docType}/${p1}/${p2}/${p3}/${safeQuery}/${salesmanParam}/${DEPARTMENT_NO}/1/999999`;

    try {
      const response = await request({ url, method: "GET" });
      const allData = response || [];
      const exportColumns = generateColumnsFromData(allData);
      const exportRows = generateRowsWithIds(allData);
      exportToCSV(exportColumns, exportRows, `${docLabel}List`);
    } catch (err) {
      console.error(`Failed to export ${docLabel}:`, err);
    }
  };

  // Handle view navigation
  const handleView = () => {
    if (selectedRowData && selectedRowData[docNoField]) {
      const stateToPass = {
        docNo: selectedRowData[docNoField],
        mode: "view",
      };

      console.log("Navigating with state:", stateToPass);

      navigate(editRoute, { state: stateToPass });
    } else {
      console.warn("No row selected or missing doc number field.");
    }
  };

  // Derived pagination values
  const currentStart =
    documentData.length === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const currentEnd =
    documentData.length === 0
      ? 0
      : Math.min((pageNumber - 1) * pageSize + documentData.length, totalCount);

  return {
    // Configuration
    config: {
      COMPANY_CODE,
      DEPARTMENT_NO,
      BASE_URL,
      REPORT_HEADER_IMAGE,
      REPORT_FOOTER_IMAGE,
      COMPANY_NAME,
      docType,
      docLabel,
      docNoField,
      CURRENT_USER,
      VATPERCENTAGE,
      ZATCASTATUS,
      IMAGE_POST_PATH,
      SERVER_PATH,
      CURRENT_USER_ROLE,
    },
    // Search state
    searchTerm,
    setSearchTerm,
    searchType,
    setSearchType,
    // UI state
    showSidebar,
    setShowSidebar,
    pageSize,
    setPageSize,
    pageNumber,
    setPageNumber,
    columnVisibilityModel,
    setColumnVisibilityModel,
    // Data state
    documentData,
    columns,
    rows,
    selectionModel,
    setSelectionModel,
    selectedRowData,
    setSelectedRowData,
    // Loading state
    loading,
    // Actions
    handleEdit,
    handleView,
    fetchDocumentList,
    refreshDocumentList, // New function to manually refresh
    exportDocumentList,
    totalCount,
    currentStart,
    currentEnd,
    isSalesOrderEditable,
    isDeliveryOrderEditable,
  };
};

export default useDocumentList;

export const exportToCSV = (columns, exportData, exportFileName = "export") => {
  if (!columns || !exportData) return;

  const csvContent = [
    columns.map((col) => col.headerName || col.field),
    ...exportData.map((row) => columns.map((col) => row[col.field] || "")),
  ];

  const csvString = csvContent
    .map((row) =>
      row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");

  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${exportFileName}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
